<template>
    <div class="OrgOpportunitiesCountCard">
        <div
            class="flex items-center space-x-2"
            :class="{ 'cursor-pointer': activeCount }"
            @click="activeCount && scrollToHash && scrollToSection(scrollToHash)"
        >
            <div>
                <CIcon :name="icon" size="large" />
            </div>
            <div>
                <strong> {{ title }} </strong>
                <div class="text-sm text-gray-800 dark:text-txt-headings">aktuálne {{ activeCount }} (celkom {{ count }}) <slot /></div>
            </div>
        </div>
        <div v-if="showActions" class="mt-2 flex w-full space-x-2">
            <CButton
                class="w-full"
                icon="settings"
                :to="actions?.settings"
                variant="primary"
            >
                Spravovať
            </CButton>
            <CButton class="w-full" icon="add" :to="actions?.add" variant="secondary"> Pridať </CButton>
        </div>
    </div>
</template>

<script setup lang="ts">
import type { RouteLocationRaw } from 'vue-router'
import type { Job } from '#shared/types/Job'
import type { Project } from '#shared/types/Project'
import type { Skill } from '#shared/types/Skill'
import { VisibilityType } from '#shared/types/visibility'
import { isPublicVolunteeringWeekDraftProject } from '#shared/utils/publicProjectFilters'

type ScrollToHashType = 'active-skills' | 'active-jobs' | 'active-projects' | ''

interface ActionsType {
    add: RouteLocationRaw
    settings: RouteLocationRaw
}

// Type definition for opportunities with visibility
type OrgOpportunityWithVisibility = Partial<Pick<Job | Project | Skill, 'visibility'>>

const props = defineProps<{
    title: string
    icon: string
    items: (Job | Skill | Project)[]
    scrollToHash?: ScrollToHashType
    actions?: ActionsType
    showActions?: boolean
}>()

// Utility function to filter public opportunities
const filterPublicOrgOpportunities = (
    opportunities: OrgOpportunityWithVisibility[]
): OrgOpportunityWithVisibility[] => {
    return opportunities.filter(
        (item: OrgOpportunityWithVisibility) =>
            item.visibility === VisibilityType.published || isPublicVolunteeringWeekDraftProject(item)
    )
}

const activeCount = computed((): number => {
    return filterPublicOrgOpportunities(props.items).length
})

const count = computed((): number => {
    return props.items.length
})

const scrollToSection = (section: string) => {
    if (!section) return

    // Implementation for smooth scrolling to a section
    const element = document.getElementById(section)
    if (element) {
        element.scrollIntoView({ behavior: 'smooth' })
    } else {
        // Fallback to hash-based navigation
        window.location.hash = `#${section}`
    }
}
</script>
